import type {
  ImpactPackage,
  ImpactPackageLink,
  ImpactPackagePage
} from '../services/impact-brief.service';
import { dashboardPageMovement, dashboardTrafficLost } from './review-queue';
import { periodChangeIsPosition, periodChangeValue } from '@taptimize/shared/dist/review/period-change-line';
import { uncapturedGapHeadlineStat, uncapturedGapStat, type UncapturedGapScale } from './uncaptured-gap';

/**
 * Presentation-only helpers for the Impact Brief packages strip.
 *
 * These live outside the component because Dashboard (IB2) and Issues (IB3)
 * render the same packages, and the plan's trust bar is that both surfaces show
 * the same ids, titles and counts. Nothing here derives a package, re-orders
 * one, or writes a title — titles and `why` are IB1's copy module (locked
 * decision 9) and are rendered verbatim. All of this only phrases numbers the
 * server already sent.
 */

/**
 * How many pages the cap left off — the "+N more" on a page package.
 *
 * Always the composer's `pagesTruncated`, never a local subtraction. A link
 * package carries `pages: []` with a `pageCount` that is context ("102 links
 * from 18 pages"), so subtracting there would claim "+18 more" over a list of
 * links; `impactPackageMoreLinks` is that package's affordance. Returns 0 for a
 * link package outright so a caller that forgets to branch gets nothing rather
 * than a wrong number.
 *
 * The clamp is there because "+-2 more" is a bug an operator has to interpret
 * while "+0 more" simply does not render.
 */
export function impactPackageMorePages(pkg: ImpactPackage | null | undefined): number {
  if (!pkg || pkg.kind === 'link') return 0;
  if (Number.isFinite(pkg.pagesTruncated as number)) {
    return Math.max(0, pkg.pagesTruncated as number);
  }
  return 0;
}

/**
 * The page rows a surface should render, and the "+N more" that goes under them.
 *
 * Two numbers that must agree, so they are derived once, together. With
 * `hideDecided` off this is exactly what the server sent — the Inventory keeps
 * every row, because looking a page up is why you are there.
 *
 * With it on (the Dashboard strip), rows already dealt with this period drop
 * out, and "+N more" has to be recounted rather than read off `pagesTruncated`:
 * a package of 18 with 16 decided shows its 2 remaining rows, and the server's
 * truncation of 13 would advertise thirteen pages of finished work as waiting.
 * The recount is against `decidedPageCount`, which counts the whole package
 * rather than the five rows the cap allowed through.
 *
 * Zero rows here means every page is decided, which is exactly when the server
 * calls the package `completed` — the strip's quiet done state, not an empty
 * package.
 */
export function impactPackagePageRows(
  pkg: ImpactPackage | null | undefined,
  hideDecided = false
): { rows: ImpactPackagePage[]; more: number } {
  if (!pkg || pkg.kind === 'link') return { rows: [], more: 0 };

  const all = pkg.pages ?? [];
  if (!hideDecided) return { rows: [...all], more: impactPackageMorePages(pkg) };

  const rows = all.filter((page) => page?.decided !== true);
  const progress = impactPackageProgress(pkg);
  const waiting = Math.max(0, progress.total - progress.decided);
  return { rows, more: Math.max(0, waiting - rows.length) };
}

/** The same affordance for a link package: `linkCount` is the uncapped total. */
export function impactPackageMoreLinks(pkg: ImpactPackage | null | undefined): number {
  if (!pkg) return 0;
  if (Number.isFinite(pkg.linksTruncated as number)) {
    return Math.max(0, pkg.linksTruncated as number);
  }
  const shown = pkg.links?.length ?? 0;
  const total = Number.isFinite(pkg.linkCount as number) ? (pkg.linkCount as number) : shown;
  return Math.max(0, total - shown);
}

/**
 * One stat in a package's header, split into the number and the word beside it.
 *
 * They are two elements rather than one string because they are two different
 * things: the number is a VALUE and takes `--font-mono` at full ink, the word is
 * LANGUAGE and takes `--font-sans` at `--text-muted`. A single "110 est. traffic
 * lost" string cannot carry two faces, and rendering the whole run in mono — as
 * the first version of this header did — makes four stats read as one
 * undifferentiated number soup.
 *
 * `wordFirst` is not decoration either. "18 pages" and "9 decaying" are counted
 * things; "lost 110" and "gap 3,491" are amounts of one thing. English puts the
 * noun after the count and the verb before the amount, and following that is
 * what stops the row reading like a spreadsheet legend.
 */
export interface ImpactPackageStat {
  key: 'pages' | 'decaying' | 'lost' | 'clicks' | 'gap' | 'links' | 'done';
  /** Already thousands-separated. Never re-derived from another stat. */
  value: string;
  word: string;
  /** true -> `lost 110`; false -> `18 pages`. */
  wordFirst: boolean;
  /** Paints the number `--danger-text`. The WORD never takes a tone. */
  danger: boolean;
}

const int = (n: number | null | undefined): number =>
  Number.isFinite(n as number) ? (n as number) : 0;

const thousands = (n: number): string => Math.round(n).toLocaleString();

/**
 * How far through a package the operator is.
 *
 * The dashboard used to be a still photograph: you could work the belt for
 * twenty minutes, come back, and the strip said exactly what it said before.
 * This is the field that lets it move — and it has to be the server's field,
 * not a local count, because this surface receives five pages of an
 * eighteen-page package and a count taken off `pages` would declare a package
 * finished after three decisions.
 *
 * `total` is `pageCount`, the full set, so the package does not shrink as it is
 * worked. A package that lost a row every time you decided one would re-rank
 * itself under the operator and destroy the record of what the week held.
 *
 * A link package has no page decisions in it and can never finish — see
 * `ImpactPackage.completed` — so it reports nothing at all rather than a
 * permanent "0 of 50 done".
 */
export interface ImpactPackageProgress {
  decided: number;
  total: number;
  /** True only when the server says so. Never inferred from the two counts. */
  completed: boolean;
  /** True when there is progress worth putting on screen. */
  started: boolean;
}

export function impactPackageProgress(
  pkg: ImpactPackage | null | undefined
): ImpactPackageProgress {
  const none = { decided: 0, total: 0, completed: false, started: false };
  if (!pkg || pkg.kind === 'link') return none;

  const total = int(pkg.pageCount);
  if (total <= 0) return none;

  // Clamped, because "19 of 18 done" is a number an operator has to stop and
  // interpret, and the honest reading of a count that overshoots its set is
  // that the set is done.
  const decided = Math.min(Math.max(0, int(pkg.decidedPageCount)), total);
  return { decided, total, completed: pkg.completed === true, started: decided > 0 };
}

/**
 * The header stats for one package, in reading order, with every gate that
 * decides whether a stat appears at all.
 *
 * The gates are the whole point of this function and each is load-bearing:
 *
 * * **pages** is always the full `pageCount`, never the capped list length. An
 *   operator sizing a week off "5 pages" when the package holds 18 would be
 *   planning against the cap instead of against the work.
 * * **decaying** is gated on `decayingPageCount` alone and deliberately *not* on
 *   `decaySum`. A package whose pages slipped on SERP position without a
 *   measurable traffic delta has `decaySum: 0` and a real count — that is a
 *   genuine bleed and hiding it behind the traffic figure would lose it.
 * * **lost** is gated on its own number for the mirror-image reason: `decaySum:
 *   0` on a decaying package means the movement was measured and cost no
 *   traffic, not that nothing moved. "lost 0" would read as a measurement of
 *   nothing rather than the absence of one.
 * * **gap** is dropped at 0 for the same reason — a gap of 0 is not a
 *   measurement worth putting beside a decay story.
 * * **links** replaces the page count on a link package, and names the uncapped
 *   total: locked decision 4 caps a link package at 15, and an operator who
 *   cannot see that 132 exist would read the cap as the whole inventory.
 */
export function impactPackageStats(
  pkg: ImpactPackage | null | undefined,
  gapScale: UncapturedGapScale | null = null
): ImpactPackageStat[] {
  if (!pkg) return [];
  const out: ImpactPackageStat[] = [];

  if (pkg.kind === 'link') {
    const shown = pkg.links?.length ?? 0;
    const total = Number.isFinite(pkg.linkCount as number) ? (pkg.linkCount as number) : shown;
    if (total) {
      out.push({
        key: 'links',
        value: total > shown && shown > 0 ? `${shown} of ${total}` : String(total),
        word: total === 1 ? 'link' : 'links',
        wordFirst: false,
        danger: false
      });
    }
  } else {
    const pages = int(pkg.pageCount);
    out.push({
      key: 'pages',
      value: String(pages),
      word: pages === 1 ? 'page' : 'pages',
      wordFirst: false,
      danger: false
    });
  }

  const decaying = int(pkg.decayingPageCount);
  if (decaying > 0) {
    out.push({
      key: 'decaying',
      value: String(decaying),
      word: 'decaying',
      wordFirst: false,
      danger: true
    });
  }

  // Where Search Console is on, the loss is its clicks and the estimate's is not shown (D1).
  const loss = dashboardTrafficLost(pkg.decaySum, pkg.searchConsoleClicksLost);
  const lost = int(loss.lost);
  if (lost > 0) {
    out.push(
      loss.source === 'search-console'
        ? { key: 'lost', value: thousands(lost), word: 'clicks lost', wordFirst: true, danger: true }
        : { key: 'lost', value: thousands(lost), word: 'lost', wordFirst: true, danger: true }
    );
  }

  const gap = int(pkg.gapSum);
  if (gap > 0) {
    // On our model, or modelled clicks where Search Console is on (D4); the strip says which once.
    out.push({ key: 'gap', ...uncapturedGapStat(gap, gapScale), danger: false });
  }

  // Progress last, because it is the only stat about the operator rather than
  // about the pages, and it is the one that changes between visits.
  //
  // Gated on having started, like every other stat here is gated on having a
  // number: "0 of 18 done" on all four packages before any work happens is a
  // row of noughts an operator learns to skip, and it would be on screen for
  // the whole of the state it least needs describing.
  //
  // A finished package is deliberately NOT "18 of 18 done" — that reads one
  // character away from "17 of 18 done", which is the state it most needs to be
  // told apart from. Finished is a marker on the header and a section that goes
  // quiet, not the top value of the same scale.
  const progress = impactPackageProgress(pkg);
  if (!progress.completed && progress.started) {
    out.push({
      key: 'done',
      value: `${progress.decided} of ${progress.total}`,
      word: 'done',
      wordFirst: false,
      danger: false
    });
  }

  return out;
}

/**
 * What moved on one page this period, for the line above its expanded panel.
 *
 * The change comes out as `periodChange.display` verbatim, except a Google position, which
 * `periodChangeValue` says in words from the two places — that string is the
 * diff layer's own rendering and DESIGN.md is explicit that nothing may
 * re-subtract it, so it is passed through as a `value` with no arithmetic
 * anywhere near it. Gap and realization sit beside it because "lost 29.8" and
 * "gap 1,240" are the two halves of whether the page is worth the week: one is
 * what it gave up, the other is what it never had.
 *
 * Empty when the page did not move and carries no demand figures — then the
 * evidence rows below are the whole answer and a blank stat line above them is
 * furniture.
 */
export function impactPageChangeStats(
  page: ImpactPackagePage | null | undefined,
  gapScale: UncapturedGapScale | null = null
): ImpactPackageStat[] {
  if (!page) return [];
  const out: ImpactPackageStat[] = [];

  // Where Search Console is on: its click change when material, never the estimate's (D1).
  const moved = dashboardPageMovement(page);
  if (moved.clicks) {
    out.push({
      key: 'clicks',
      value: moved.clicks.display,
      word: 'Search Console clicks',
      wordFirst: true,
      danger: moved.clicks.change < 0
    });
  }
  if (moved.periodChange) {
    // A Google position reads in words under its own label, as on `/review`: "lost 7 (+1)" read
    // like a gain (`period-change-line.ts`).
    const change = moved.periodChange;
    out.push({
      key: 'lost',
      value: periodChangeValue(change),
      word: periodChangeIsPosition(change) ? change.metricLabel : change.direction === 'decay' ? 'lost' : 'gained',
      wordFirst: true,
      danger: change.direction === 'decay'
    });
  }
  if (page.gap !== null && Number.isFinite(page.gap)) {
    out.push({ key: 'gap', ...uncapturedGapStat(page.gap as number, gapScale), danger: false });
  }
  if (page.realizationRatio !== null && Number.isFinite(page.realizationRatio)) {
    out.push({
      key: 'pages',
      value: `${Math.round((page.realizationRatio as number) * 100)}%`,
      word: 'realized',
      wordFirst: false,
      danger: false
    });
  }
  return out;
}

/**
 * What kind of thing a package is scoped to.
 *
 * Three genuinely different scopes reach the header as one string. A cluster
 * ("Project management & delivery") is a topic somebody defined; a path prefix
 * ("/templates") is a subfolder the composer found by grouping unclustered
 * decay; and neither means the pages have nothing in common but their trouble.
 * On the live ManyRequests brief the first two sit adjacent and read as the same
 * kind of heading in two writing styles, which is the whole confusion.
 */
export type ImpactPackageScopeKind = 'topic' | 'section' | 'unassigned';

export interface ImpactPackageHeading {
  /** The scope half of `title` — what the package is about. */
  name: string;
  /** The action half, rendered as the small eyebrow behind it. Null if absent. */
  kind: string | null;
  scope: ImpactPackageScopeKind;
  /** The qualifier rendered ahead of the name. Uppercased by CSS, not here. */
  scopeLabel: string;
}

const SCOPE_LABELS: Record<ImpactPackageScopeKind, string> = {
  topic: 'Topic',
  section: 'Section',
  unassigned: 'Unassigned'
};

/**
 * Which of the three scopes a package has, read off structured fields.
 *
 * `pathPrefix` is only ever set on a `decay-prefix` package, so it is the
 * unambiguous signal and is checked first. Deriving this from the title string
 * instead — "does it start with a slash?" — would make a cluster somebody named
 * "/templates rewrite" render as a subfolder.
 */
export function impactPackageScope(
  pkg: ImpactPackage | null | undefined
): ImpactPackageScopeKind {
  if ((pkg?.pathPrefix || '').trim()) return 'section';
  if ((pkg?.clusterLabel || '').trim()) return 'topic';
  return 'unassigned';
}

/**
 * A package title split into the thing, the verdict on it, and what kind of
 * thing it is.
 *
 * IB1 composes `title` as `<action> — <label>`: "Recover lost traffic —
 * /templates". The header renders the label first and large, with the action
 * behind it as a small uppercase eyebrow, because across four packages the
 * labels are what differ and the actions repeat — three of ManyRequests' four
 * packages open with the decay phrase, so leading with it puts the same words at
 * the top of three sections.
 *
 * The scope qualifier is the other half of that: ahead of the name, `TOPIC` and
 * `SECTION` say which of two unlike things the name is, and the name takes the
 * matching face — a path prefix in mono, like every path in the rows below it, a
 * cluster in sans, because it is language.
 *
 * **This splits the server's string; it never composes one.** Locked decision 9
 * says the copy is the backend's, and it still is — every character of `name`
 * and `kind` came from `title`, in the order the header wants rather than the
 * order the string has. On the FIRST em dash only, so a label containing one
 * survives intact, and a title without one is returned whole with no eyebrow
 * rather than guessed at. `scopeLabel` is the one word this module adds, and it
 * names a payload field rather than describing the pages.
 */
export function impactPackageHeading(
  pkg: ImpactPackage | null | undefined
): ImpactPackageHeading {
  const scope = impactPackageScope(pkg);
  const scopeLabel = SCOPE_LABELS[scope];
  const title = (pkg?.title || '').trim();
  const at = title.indexOf('—');
  if (at < 0) return { name: title, kind: null, scope, scopeLabel };
  const kind = title.slice(0, at).trim();
  const name = title.slice(at + 1).trim();
  if (!kind || !name) return { name: title, kind: null, scope, scopeLabel };
  return { name, kind, scope, scopeLabel };
}

/**
 * The path to show for a page row.
 *
 * The composer precomputes `path`, so Dashboard and Issues cannot format one URL
 * two ways. Deriving from the URL is a fallback for a payload that omits it, and
 * the raw string is the last resort — a page whose URL will not parse still
 * belongs on screen.
 */
export function impactPackagePagePath(
  page: ImpactPackagePage | null | undefined,
  maxLength = 64
): string {
  if (!page) return '';
  const fromServer = (page.path || '').trim();
  const raw = fromServer || (page.url || '').trim();
  if (!raw) return '';

  let display = raw;
  if (!fromServer) {
    try {
      const parsed = new URL(raw);
      display = parsed.pathname + parsed.search || '/';
    } catch {
      display = raw;
    }
  }

  return display.length > maxLength ? display.slice(0, maxLength - 1) + '…' : display;
}

/**
 * The whole brief's totals, for the strip's own header: the same two amounts
 * every package header carries, summed by IB1 across all of them.
 *
 * Read off `headline`'s structured fields rather than off its sentence.
 * `headline.text` says the same thing in prose — "…25 pages lost ground this
 * period worth an estimated 358 monthly visits; 5,868 monthly visits of demand
 * sits uncaptured" — and a two-line sentence above a table of the same numbers
 * is the same content twice in two framings.
 */
export function impactBriefHeadlineStats(
  headline:
    | {
        trafficDecaySum: number | null;
        searchConsoleClicksLost?: number | null;
        gapSum: number | null;
        gapScale?: UncapturedGapScale | null;
      }
    | null
    | undefined
): ImpactPackageStat[] {
  if (!headline) return [];
  const out: ImpactPackageStat[] = [];

  // Where Search Console is on, the loss is its clicks, and null when it could not measure the
  // period: the stat drops and the Dashboard's header says why once. Never the estimate (D1).
  const loss = dashboardTrafficLost(headline.trafficDecaySum, headline.searchConsoleClicksLost);
  if (loss.source === 'search-console') {
    if (loss.lost !== null && Number.isFinite(loss.lost)) {
      out.push({
        key: 'lost',
        value: thousands(loss.lost),
        word: 'clicks lost',
        wordFirst: true,
        danger: loss.lost > 0
      });
    }
  } else if (headline.trafficDecaySum !== null && Number.isFinite(headline.trafficDecaySum)) {
    // Null and 0 are different answers here and the payload says so in as many
    // words: null is `issues-potential` mode, where no pair was ever measured; 0
    // is a measured period whose decay was SERP-only. A stat that renders "traffic
    // lost 0" for both claims a measurement that never happened, so null drops the
    // stat and 0 keeps it.
    out.push({
      key: 'lost',
      value: thousands(headline.trafficDecaySum),
      word: 'traffic lost',
      wordFirst: true,
      danger: headline.trafficDecaySum > 0
    });
  }
  if (headline.gapSum !== null && Number.isFinite(headline.gapSum)) {
    // The unit is said here, once: modelled clicks where Search Console is on, else our model (D4).
    out.push({ key: 'gap', ...uncapturedGapHeadlineStat(headline.gapSum, headline.gapScale), danger: false });
  }
  return out;
}

/**
 * Why the strip has no cards, in the words that fit the actual reason.
 *
 * The three states look alike and are not: a domain nobody has analysed needs a
 * crawl, a queue that produced no package still has rows worth reading, and a
 * null state is a workspace with nothing in it at all. One safe phrase covering
 * all three would send an operator to the wrong place in two of them.
 */
export function impactBriefEmptyMessage(state: string | null | undefined): string {
  switch (state) {
    case 'no_analysis':
      return 'This domain has not been analysed yet. Run a crawl and the week plan fills in.';
    case 'no_packages':
      return 'Nothing grouped into a work package this period — the Issues queue still has rows worth a look.';
    default:
      return 'No work packages for this period yet.';
  }
}

/**
 * What the last column is called, and what it holds (FW-FIRSTRUN-01 §4 B4).
 *
 * On a monitoring domain it is CHANGE and it carries `periodChange.display`
 * verbatim, which is the diff layer's own rendering.
 *
 * On a first crawl there is no pair, so `periodChange` is null on every row and
 * the column renders a dash 250 times. Meanwhile `gap` is sitting on the same
 * rows ranging from 34 to 9,059 — the only priority signal the page has, and
 * the column withholding it is what makes a page worth 9,059 visits look like
 * one worth 34. So the column takes the other name and the number already
 * there. Nothing new is computed; `gap` is read off the row the server sent.
 */
export function impactChangeColumnLabel(mode: string | null | undefined): string {
  return mode === 'issues-potential' ? 'Opportunity' : 'Change';
}

/**
 * The last column's value for one page row.
 *
 * The em dash stays the answer when there is genuinely nothing to say — an
 * unscored gap on a first crawl is not an opportunity of zero, and writing 0
 * there would state a measurement nobody took.
 */
export function impactPageChangeCell(
  page: ImpactPackagePage | null | undefined,
  mode: string | null | undefined
): string {
  if (!page) return '—';
  // Where Search Console is on: its material click change, else a SERP-position change, never the
  // estimate (D1). "clicks" names the unit, so it is not read as the estimate's number.
  const moved = dashboardPageMovement(page);
  if (moved.clicks) return `${moved.clicks.display} clicks`;
  if (moved.periodChange) return periodChangeValue(moved.periodChange);
  if (mode === 'issues-potential') {
    const gap = page.gap;
    if (typeof gap === 'number' && Number.isFinite(gap) && gap > 0) return thousands(gap);
  }
  return '—';
}

/**
 * The tone of the last column, from the movement it shows (`impactPageChangeCell`): a page whose
 * estimate fell but whose clicks did not move materially shows no change, so it is not red.
 */
export function impactPageChangeTone(page: ImpactPackagePage | null | undefined): string {
  if (!page) return 'change-flat';
  const moved = dashboardPageMovement(page);
  if (moved.clicks) return moved.clicks.change < 0 ? 'change-down' : 'change-up';
  if (moved.periodChange) return moved.periodChange.direction === 'decay' ? 'change-down' : 'change-up';
  return 'change-flat';
}

/**
 * The line under the strip's title (FW-FIRSTRUN-01 §4 B5).
 *
 * Monitoring is unchanged: "4 packages · 45 pages · 25 lost ground". There
 * `pageCount` and the belt differ only by pages listed in two packages, and the
 * period's story is the movement.
 *
 * On a first crawl they differ by an order that matters. `pageCount` sums every
 * package, and a link package contributes its SOURCE pages — 50 and 23 on
 * slidespeak — which the belt never opens, because a link row names two URLs
 * and a phrase rather than a page to review. So the strip said "122 pages" over
 * a button offering "Review 49 pages", and B1–B3 adding packages widened the
 * gap rather than closing it. Two numbers an operator reads as one claim, one
 * of them disprovable by pressing the other.
 *
 * The line counts the belt instead, and names what was held back rather than
 * leaving 69 items to vanish between "250 in the queue" and what is on screen.
 */
export function impactBriefSummary(
  packageCount: number,
  headline:
    | { pageCount: number; reviewablePageCount?: number; decayingPageCount: number }
    | null
    | undefined,
  mode: string | null | undefined,
  totals: { excludedPageCount: number } | null | undefined
): string {
  const parts = [`${packageCount} ${packageCount === 1 ? 'package' : 'packages'}`];
  if (!headline) return parts.join(' · ');

  const reviewable = headline.reviewablePageCount;
  if (mode === 'issues-potential' && typeof reviewable === 'number' && Number.isFinite(reviewable)) {
    parts.push(`${reviewable} ${reviewable === 1 ? 'page' : 'pages'} to review`);
    const held = totals?.excludedPageCount ?? 0;
    if (held > 0) parts.push(`${held} not packaged`);
    return parts.join(' · ');
  }

  const pages = headline.pageCount;
  parts.push(`${pages} ${pages === 1 ? 'page' : 'pages'}`);
  if (headline.decayingPageCount > 0) {
    parts.push(`${headline.decayingPageCount} lost ground`);
  }
  return parts.join(' · ');
}

/**
 * What the strip calls itself (FW-FIRSTRUN-01, follow-up to §4 B4/B5).
 *
 * "This week's impact" is a promise about a period, and a period needs two
 * crawls to exist. On a domain with one crawl there is no week to compare, no
 * movement to report and nothing that happened *this* week rather than any
 * other — the strip is showing what the queue is worth, not what changed. It
 * follows `mode` for the same reason the last column does.
 */
export function impactStripHeading(mode: string | null | undefined): string {
  return mode === 'issues-potential' ? 'What to work on first' : 'This week’s impact';
}

/**
 * The one row a link package collapses to (§4 B6).
 *
 * A link package renders up to `IMPACT_BRIEF_MAX_LINKS_PER_PACKAGE` rows of
 * source → target → anchor. Two of them on slidespeak's first screen is thirty
 * rows of anchor suggestions against twelve rows of page work, so the screen
 * reads as a link inventory with some pages attached — which is the noise B6
 * names.
 *
 * It says what pressing it reveals rather than repeating the package header,
 * which already carries the uncapped total ("132 links"). The wording is
 * `packageWhy`'s: the top N are the ones ready to paste. It names the pages
 * too, since pressing it now reveals one row per page (§0az).
 */
export function impactLinkRowSummary(pkg: ImpactPackage | null | undefined): string {
  const shown = pkg?.links?.length ?? 0;
  const pages = impactLinkPages(pkg).length;
  const on = pages > 0 ? ` on ${plural(pages, 'page')}` : '';
  return `${plural(shown, 'link')}${on} ready to paste`;
}

/** One source page and the links to add to it — one visit (§0az). */
export interface ImpactLinkPage {
  sourceUrlId: string;
  sourceUrl: string;
  sourcePath: string;
  links: ImpactPackageLink[];
  /**
   * The page needs exactly one link, so its heading row and its only link row
   * are the same row (§0az follow-up).
   *
   * Grouping alone made the expanded list TALLER than the flat one it
   * replaced — 11 page rows plus 15 link lines where there had been 15 rows —
   * because a page with one link spent two rows saying it. On the live
   * manyrequests brief 7 of 11 pages in the largest package are single-link,
   * so this is most of the height back, and the surface's whole job is a
   * capacity-sized skim.
   */
  inline: boolean;
}

/**
 * A link package's links, one entry per source page (§0az).
 *
 * Every other row on the surface is a page. A flat list of pairs put
 * `/blog/wrike-alternatives` on seven rows and left the operator to notice the
 * repetition and batch it by hand; the work is one visit with seven edits.
 *
 * Grouped here by `sourceUrlId` in first-seen order. The composer already
 * sends a page's links together (`selectLinksByPage`), so this preserves its
 * order; it groups rather than trusting adjacency so a payload that ever
 * interleaves cannot put one page on two rows again.
 */
export function impactLinkPages(pkg: ImpactPackage | null | undefined): ImpactLinkPage[] {
  const byPage = new Map<string, ImpactLinkPage>();
  for (const link of pkg?.links ?? []) {
    const page = byPage.get(link.sourceUrlId);
    if (page) {
      page.links.push(link);
      // A second link means the page needs a heading of its own after all.
      page.inline = false;
    } else {
      byPage.set(link.sourceUrlId, {
        sourceUrlId: link.sourceUrlId,
        sourceUrl: link.sourceUrl,
        sourcePath: link.sourcePath,
        links: [link],
        inline: true
      });
    }
  }
  return [...byPage.values()];
}

/**
 * "+48 more links across 31 pages in this package", or ''.
 *
 * Both units, because the cap counts links and the work is counted in visits:
 * "+48 more links" alone cannot tell five pages of heavy editing from
 * forty-eight separate trips. The page count is the composer's
 * `linkPagesTruncated`, never `pageCount` minus the pages shown — a page the
 * cap cut in two is in both. A payload from before the field existed gets the
 * links-only line rather than a guessed page count.
 */
export function impactLinkMoreLabel(pkg: ImpactPackage | null | undefined): string {
  const n = impactPackageMoreLinks(pkg);
  if (n <= 0) return '';
  const pages = pkg?.linkPagesTruncated;
  const across =
    typeof pages === 'number' && Number.isFinite(pages) && pages > 0
      ? ` across ${plural(pages, 'page')}`
      : '';
  return `+${n} more ${n === 1 ? 'link' : 'links'}${across} in this package`;
}

function plural(n: number, word: string): string {
  return `${n} ${n === 1 ? word : `${word}s`}`;
}
